import * as styles from "./ProjectList.css";

import { Provider, createStore, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Heading } from "@frontend/components/atoms/Heading";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { Stack } from "@frontend/components/atoms/Stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { Pagination } from "@frontend/components/molecules/Pagination";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
} from "@frontend/components/organisms/DataTable";
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderHeading,
} from "@frontend/components/organisms/PageHeader";
import { Tour, useTour } from "@frontend/components/organisms/Tour";
import { useDebouncedCallback } from "@frontend/hooks/useDebouncedCallback";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { useRowNavigation } from "@frontend/hooks/useRowNavigation";
import { ClientCombobox } from "@frontend/shared/client-field";
import { formatAmount } from "@frontend/utils/amount";
import { formatMinutes } from "@frontend/utils/duration";
import { Trans, useLingui } from "@lingui/react/macro";
import type { SortingState } from "@tanstack/react-table";

import { ProjectEditorDialog } from "./ProjectEditorDialog";
import { ProjectQuickview } from "./components/ProjectQuickview";
import { useProjectListTour } from "./hooks/useProjectListTour";
import { useProjectQuickviewTour } from "./hooks/useProjectQuickviewTour";
import {
  archiveProjectAtom,
  deleteProjectAtom,
  loadProjectListOptionsAtom,
  prefetchProjectListAtom,
  projectListAtom,
  reloadProjectListAtom,
  setProjectListQueryAtom,
} from "./stores/list";
import type { ProjectListState } from "./stores/list";
import {
  closeProjectQuickviewAtom,
  openProjectQuickviewAtom,
  refreshProjectQuickviewAtom,
  shownProjectQuickviewIdAtom,
} from "./stores/quickview";
import type {
  ProjectListColumn,
  ProjectListFilter,
  ProjectRow,
  ProjectType,
} from "./types";
import {
  openHoursSummary,
  openHoursValidation,
  schedulerUrl,
} from "./utils/windows";
import { projectListViews } from "./views";
import type { ProjectColumnId } from "./views";

const helper = createDataTableColumnHelper<ProjectRow>();

const PAGE_SIZES = [10, 25, 50, 100] as const;

const SEARCH_DEBOUNCE = 300;

export type Opened = {
  projectId: number | null;
  type: ProjectType;
};

function toSortQuery(
  sorting: SortingState,
): Pick<ProjectListState, "sort" | "direction"> {
  const [first] = sorting;

  return {
    sort: (first?.id as ProjectListColumn | undefined) ?? null,
    direction: first?.desc ? "desc" : "asc",
  };
}

export type ProjectListProps = {
  type?: ProjectType | "all";
  onOpen?: (opened: Opened) => void;
};

export function ProjectList({
  type = "all",
  onOpen,
}: ProjectListProps): JSX.Element {
  const { t, i18n } = useLingui();

  const _ = i18n._.bind(i18n);

  const locale = useIntlLocale();

  const pinned = "all" === type ? null : type;

  const {
    rows,
    total,
    page,
    perPage,
    search,
    type: kind,
    filter,
    sort,
    direction,
    criteria,
    filterOptions,
    loading,
    error,
  } = useAtomValue(projectListAtom);

  const reload = useSetAtom(reloadProjectListAtom);
  const loadOptions = useSetAtom(loadProjectListOptionsAtom);
  const setQuery = useSetAtom(setProjectListQueryAtom);
  const prefetch = useSetAtom(prefetchProjectListAtom);
  const archive = useSetAtom(archiveProjectAtom);
  const remove = useSetAtom(deleteProjectAtom);
  const openQuickview = useSetAtom(openProjectQuickviewAtom);
  const closeQuickview = useSetAtom(closeProjectQuickviewAtom);
  const refreshQuickview = useSetAtom(refreshProjectQuickviewAtom);
  const shownProjectId = useAtomValue(shownProjectQuickviewIdAtom);

  const pageCount = Math.max(1, Math.ceil(total / perPage));

  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const [opened, setOpened] = useState<Opened | null>(null);

  const opener = useRef(onOpen);

  useEffect(() => {
    opener.current = onOpen;
  });

  const open = useCallback(
    (next: Opened) => {
      (opener.current ?? setOpened)(next);
    },
    [setOpened],
  );
  const [pendingDelete, setPendingDelete] = useState<ProjectRow | null>(null);

  const pendingDeleteLabel = pendingDelete?.label ?? "";

  const [quickviewOpen, setQuickviewOpen] = useState(false);

  const showQuickview = (project: ProjectRow) => {
    setQuickviewOpen(true);
    void openQuickview(project.id);
  };

  // Arrows walk the rows and open each one as they land, pages included.
  const navigation = useRowNavigation({
    rowCount: rows.length,
    page,
    pageCount,
    onPageChange: (next) => setQuery({ page: next }),
    onOpen: (index) => {
      const project = rowsRef.current[index];

      if (project) {
        showQuickview(project);
      }
    },
  });

  const tour = useTour(useProjectListTour());
  const quickviewTour = useTour(useProjectQuickviewTour());

  const [typed, setTyped] = useState(search);

  const hideQuickview = () => {
    setQuickviewOpen(false);
    closeQuickview();
    navigation.setActiveIndex(-1);
  };

  const searchLater = useDebouncedCallback(
    (next: string) => void setQuery({ search: next }),
    SEARCH_DEBOUNCE,
  );

  useEffect(() => {
    void setQuery({ type: pinned });
    void loadOptions();
  }, [setQuery, loadOptions, pinned]);

  const sorting = useMemo(
    () => (null === sort ? [] : [{ id: sort, desc: "desc" === direction }]),
    [sort, direction],
  );

  const views = useMemo(
    () => projectListViews(pinned ?? kind ?? "all"),
    [pinned, kind],
  );

  const [picked, setPicked] = useState<string | null>(null);

  const view = views.find((candidate) => candidate.id === picked) ?? views[0]!;

  const columns = useMemo(() => {
    const defined = {
      id: helper.accessor("id", {
        header: t`Id`,
        meta: { width: "90px", filter: "number" },
      }),
      clientLabel: helper.accessor("clientLabel", {
        header: t`Client`,
        meta: {
          minWidth: "120px",
          truncate: true,
          filter: {
            type: "picker",
            render: ({ value, onValueChange }) => (
              <ClientCombobox
                size="sm"
                value={null === value ? null : Number(value)}
                onValueChange={(client) =>
                  onValueChange(client ? String(client.id) : null)
                }
              />
            ),
          },
        },
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      label: helper.accessor("label", {
        header: t`Name`,
        meta: { minWidth: "120px", truncate: true, filter: "text" },
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      mainAccountantLabel: helper.accessor("mainAccountantLabel", {
        header: t`Person in charge`,
        meta: { minWidth: "120px", truncate: true, filter: "text" },
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      type: helper.accessor("type", {
        header: t`Kind`,
        enableSorting: false,
        meta: { minWidth: "100px" },
        cell: ({ getValue }) => (
          <Badge
            variant={"subscription" === getValue() ? "info" : "neutral"}
            size="sm"
          >
            {"subscription" === getValue() ? t`Subscription` : t`Intervention`}
          </Badge>
        ),
      }),
      expectedDate: helper.accessor("expectedDate", {
        header: t`Date`,
        meta: { width: "160px", filter: "dateRange" },
        cell: ({ getValue }) => date(getValue(), locale) ?? "-",
      }),
      stateLabel: helper.accessor("stateLabel", {
        header: t`State`,
        enableSorting: false,
        meta: {
          minWidth: "80px",
          maxWidth: "180px",
          truncate: true,
          filter: { type: "select", options: filterOptions.states },
        },
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      estimatedMinutes: helper.accessor("estimatedMinutes", {
        header: t`Planned hours`,
        meta: { width: "130px" },
        cell: ({ getValue }) => hours(getValue()),
      }),
      validatedMinutes: helper.accessor("validatedMinutes", {
        header: t`Validated hours`,
        meta: { width: "140px" },
        cell: ({ getValue }) => hours(getValue()),
      }),
      estimatedPrice: helper.accessor("estimatedPrice", {
        header: t`Price`,
        meta: { width: "130px" },
        cell: ({ getValue }) => amount(getValue(), locale),
      }),
      amountToBill: helper.accessor("amountToBill", {
        header: t`To bill`,
        meta: { width: "130px" },
        cell: ({ getValue }) => amount(getValue(), locale),
      }),
      billedPrice: helper.accessor("billedPrice", {
        header: t`Billed`,
        meta: { width: "130px" },
        cell: ({ getValue }) => amount(getValue(), locale),
      }),
      billedHourPrice: helper.accessor("billedHourPrice", {
        header: t`Hourly rate`,
        meta: { width: "130px" },
        cell: ({ getValue }) => amount(getValue(), locale),
      }),
      balance: helper.accessor("balance", {
        header: t`Balance`,
        meta: { width: "130px" },
        cell: ({ getValue }) => amount(getValue(), locale),
      }),
      serviceGroupLabel: helper.accessor("serviceGroupLabel", {
        header: t`Department`,
        meta: {
          width: "150px",
          filter: { type: "select", options: filterOptions.serviceGroups },
        },
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      statusLabel: helper.accessor("statusLabel", {
        header: t`Status`,
        meta: {
          minWidth: "90px",
          maxWidth: "180px",
          truncate: true,
          filter: { type: "select", options: filterOptions.statuses },
        },
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      actions: helper.display({
        id: "actions",
        header: t`Action`,
        enableSorting: false,
        meta: { width: "150px" },
        cell: ({ row }) => (
          <span
            className={styles.rowActions}
            // The row itself opens the drawer, and these do something else.
            onClick={(event) => event.stopPropagation()}
          >
            {row.original.canValidateHours ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Hours validation`}
                      onClick={() =>
                        openHoursValidation(
                          row.original.id,
                          t`Hours validation`,
                        )
                      }
                    >
                      <Icon name="Clock" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="top">
                  {t`Hours validation`}
                </TooltipContent>
              </Tooltip>
            ) : null}

            {"intervention" === row.original.type ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Show in the planning`}
                      render={<a href={schedulerUrl(row.original.id)} />}
                    >
                      <Icon name="CalendarDays" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="top">
                  {t`Show in the planning`}
                </TooltipContent>
              </Tooltip>
            ) : null}

            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t`Hours summary`}
                    onClick={() =>
                      openHoursSummary(row.original.id, t`Hours summary`)
                    }
                  >
                    <Icon name="FileText" size="sm" />
                  </Button>
                }
              />

              <TooltipContent side="top">{t`Hours summary`}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t`Edit`}
                    onClick={() =>
                      open({
                        projectId: row.original.id,
                        type: row.original.type,
                      })
                    }
                  >
                    <Icon name="Pencil" size="sm" />
                  </Button>
                }
              />

              <TooltipContent side="top">{t`Edit`}</TooltipContent>
            </Tooltip>
          </span>
        ),
      }),
    } satisfies Record<ProjectColumnId, unknown>;

    return helper.columns(view.columns.map((id) => defined[id]));
  }, [t, locale, filterOptions, view, open]);

  const title =
    "subscription" === pinned
      ? t`Subscriptions`
      : "intervention" === pinned
        ? t`Interventions`
        : t`Projects`;

  return (
    <Stack gap="md">
      <PageHeader variant="plain" icon="Network">
        <PageHeaderContent>
          <PageHeaderHeading>
            <Heading name={title} />
          </PageHeaderHeading>
        </PageHeaderContent>

        <PageHeaderActions>
          {"intervention" === pinned ? null : (
            <Button
              variant="link"
              leftSection={<Icon name="Wand2" size="sm" />}
              onClick={() => open({ projectId: null, type: "subscription" })}
            >
              {t`New subscription`}
            </Button>
          )}

          {"subscription" === pinned ? null : (
            <Button
              variant="link"
              leftSection={<Icon name="Wand2" size="sm" />}
              onClick={() => open({ projectId: null, type: "intervention" })}
            >
              {t`New intervention`}
            </Button>
          )}

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  aria-label={t`How this page works`}
                  onClick={() => tour.start({ force: true })}
                >
                  <Icon name="CircleHelp" size="sm" />
                </Button>
              }
            />

            <TooltipContent side="bottom">
              {t`How this page works`}
            </TooltipContent>
          </Tooltip>
        </PageHeaderActions>
      </PageHeader>

      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      {/* Visible overflow: the filter popovers of the header row open out of
          the panel. */}
      <Panel overflow="visible">
        {/* One line: the search gives up its room to the three pickers rather
            than pushing them onto a second row. */}
        <Toolbar wrap={false}>
          <ToolbarSection className={styles.filters}>
            <Input
              className={styles.search}
              value={typed}
              placeholder={t`Search a name, a client, an identifier`}
              leftSection={<Icon name="Search" size="sm" />}
              clearable
              onValueChange={(next) => {
                setTyped(next);
                searchLater(next);
              }}
            />

            {null === pinned ? (
              <Select
                items={[
                  { label: t`Every kind`, value: "all" },
                  { label: t`Subscriptions`, value: "subscription" },
                  { label: t`Interventions`, value: "intervention" },
                ]}
                value={kind ?? "all"}
                onValueChange={(next) =>
                  void setQuery({
                    type: "all" === next ? null : (next as ProjectType),
                  })
                }
              >
                <SelectTrigger className={styles.filter}>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">{t`Every kind`}</SelectItem>
                  <SelectItem value="subscription">
                    {t`Subscriptions`}
                  </SelectItem>
                  <SelectItem value="intervention">
                    {t`Interventions`}
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : null}

            <Select
              items={[
                { label: t`Active`, value: "active" },
                { label: t`Archived`, value: "archived" },
                { label: t`All`, value: "all" },
              ]}
              value={filter}
              onValueChange={(next) =>
                void setQuery({ filter: next as ProjectListFilter })
              }
            >
              <SelectTrigger className={styles.filter}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="active">{t`Active`}</SelectItem>
                <SelectItem value="archived">{t`Archived`}</SelectItem>
                <SelectItem value="all">{t`All`}</SelectItem>
              </SelectContent>
            </Select>
          </ToolbarSection>

          <ToolbarSection>
            {/* One view leaves nothing to pick. */}
            {1 < views.length ? (
              <Field orientation="horizontal" className={styles.view}>
                <FieldLabel>{t`View`}</FieldLabel>

                <FieldContent>
                  <Select
                    items={views.map((candidate) => ({
                      label: _(candidate.label),
                      value: candidate.id,
                    }))}
                    value={view.id}
                    onValueChange={(next) => setPicked(next)}
                  >
                    <SelectTrigger className={styles.viewPicker}>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      {views.map((candidate) => (
                        <SelectItem key={candidate.id} value={candidate.id}>
                          {_(candidate.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldContent>
              </Field>
            ) : null}

            <Select
              items={PAGE_SIZES.map((size) => ({
                label: String(size),
                value: String(size),
              }))}
              value={String(perPage)}
              onValueChange={(next) => void setQuery({ perPage: Number(next) })}
            >
              <SelectTrigger className={styles.pageSize}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarSection>
        </Toolbar>

        <DataTable
          columns={columns}
          data={rows}
          getRowId={(project) => String(project.id)}
          loading={loading}
          loadingRowCount={perPage}
          emptyMessage={<Trans>No project matches this search.</Trans>}
          columnBorders
          bordered={false}
          scroll="page"
          stickyHeader
          stickyOffset="var(--layout-header-height, 0px)"
          sorting={sorting}
          onSortingChange={(next) => void setQuery(toSortQuery(next))}
          onSortPrefetch={(next) => prefetch({ page: 1, ...toSortQuery(next) })}
          columnFilters={criteria}
          onColumnFiltersChange={(next) => void setQuery({ criteria: next })}
          activeRowIndex={navigation.activeIndex}
          onRowClick={(project) => {
            navigation.setActiveIndex(rows.indexOf(project));
            showQuickview(project);
          }}
          rowMenu={(project) => [
            { id: "edit", label: t`Edit`, icon: "Pencil" },
            {
              id: "archive",
              label: t`Archive`,
              icon: "Archive",
              disabled: project.archived,
            },
            {
              id: "delete",
              label: t`Delete`,
              icon: "Trash2",
              separatorBefore: true,
              disabled: !project.deletable,
            },
          ]}
          onRowMenuSelect={(id, project) => {
            if ("edit" === id) {
              open({ projectId: project.id, type: project.type });
            }

            if ("archive" === id) {
              void archive(project.id);
            }

            if ("delete" === id) {
              setPendingDelete(project);
            }
          }}
        />
      </Panel>

      <Pagination
        page={page}
        pageCount={pageCount}
        totalItems={total}
        pageSize={perPage}
        align="end"
        onPageChange={(next) => void setQuery({ page: next })}
        onPagePrefetch={(next) => prefetch({ page: next })}
      />

      <ProjectQuickview
        open={quickviewOpen}
        onOpenChange={(next) => {
          if (!next) {
            hideQuickview();
          }
        }}
        onEdit={(projectId) => {
          const row = rows.find((project) => projectId === project.id);

          open({
            projectId,
            type: row?.type ?? "subscription",
          });
        }}
        onHelp={() => quickviewTour.start({ force: true })}
      />

      {undefined !== onOpen || null === opened ? null : (
        <ProjectWindow
          key={opened.projectId ?? `new-${opened.type}`}
          opened={opened}
          onClose={() => {
            setOpened(null);
            void refreshQuickview();
          }}
          onSaved={() => {
            setOpened(null);
            void reload();
          }}
        />
      )}

      <AlertDialog
        open={null !== pendingDelete}
        onOpenChange={(next) => {
          if (!next) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this project?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                {pendingDeleteLabel} goes with its work posts and their tasks.
                This cannot be undone.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) {
                  if (pendingDelete.id === shownProjectId) {
                    hideQuickview();
                  }

                  void remove(pendingDelete.id);
                }

                setPendingDelete(null);
              }}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Tour {...tour.props} />
      <Tour {...quickviewTour.props} />
    </Stack>
  );
}

function date(value: string | null, locale: string): string | null {
  if (null === value) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00`);

  return Number.isNaN(parsed.getTime())
    ? null
    : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(parsed);
}

function hours(minutes: number | null): string {
  return null === minutes ? "-" : formatMinutes(minutes);
}

function amount(value: number | null, locale: string): string {
  return null === value ? "-" : formatAmount(value, locale);
}

function ProjectWindow({
  opened,
  onClose,
  onSaved,
}: {
  opened: Opened;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [store] = useState(() => createStore());

  return (
    <Provider store={store}>
      <ProjectEditorDialog
        open
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
        projectId={opened.projectId}
        type={opened.type}
        onSaved={onSaved}
        onCancel={onClose}
      />
    </Provider>
  );
}
